<!--
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
-->
<div class="container-fluid products">
  <mat-tab-group class="instructions" *ngIf="(isInternal && !impersonatedUserName) || currentUserService.isAdmin()">
    <mat-tab label="Add Products" *ngIf="!impersonatedUserName" id="products-tab">
      <div class="instruction">
        Please use the pull-down boxes below to add releases to your Wind River Gallery account. If you do not see the
        product release you are looking for, please contact the Product Management owner for that release.
      </div>
      <div class="instruction">
        If you need to remove a release, press the "Remove" button on its release card. If the release has been added
        through an official customer licence number and is not expired, then the release will be deactivated but will
        not be removed completely. Please file a
        <a href="https://jira.wrs.com/secure/CreateIssue.jspa?pid=13802&issuetype=2" target="_blank">Jira issue</a> to
        remove the license from your account.
      </div>
      <div class="instruction">Note: Adding releases may take a few minutes.</div>
      <div>
        <div class="selectors">
          <mat-form-field class="select-release-type">
            <mat-select [(ngModel)]="selectedReleaseType" (selectionChange)="onSelectReleaseType($event.value)">
              <mat-option value="release">Release</mat-option>
              <mat-option value="ear">EAR/Other</mat-option>
              <mat-option value="spin">Under Development</mat-option>
            </mat-select>
            <mat-hint>Select release type</mat-hint>
          </mat-form-field>

          <mat-progress-spinner
            *ngIf="loadingProducts"
            color="primary"
            mode="indeterminate"
            diameter="58"
          ></mat-progress-spinner>
          <wsn-searchable-select
            class="select-product"
            *ngIf="!loadingProducts"
            (onChangeSelection)="onSelectProduct($event)"
            [allItems]="allProducts"
            [selected]="selectedProduct"
            searchProperty="name"
            hint="Select a product"
          ></wsn-searchable-select>

          <mat-progress-spinner
            *ngIf="loadingReleases"
            color="primary"
            mode="indeterminate"
            diameter="58"
          ></mat-progress-spinner>
          <wsn-searchable-select
            class="select-release"
            *ngIf="!loadingReleases && selectedProduct"
            (onChangeSelection)="onSelectRelease($event)"
            [allItems]="filteredReleases"
            [selected]="selectedRelease"
            searchProperty="name"
            hint="Select a release"
            [showLongNameTooltip]="true"
          ></wsn-searchable-select>

          <mat-progress-spinner
            id="product-spinner"
            *ngIf="addingCommItems"
            color="primary"
            mode="indeterminate"
            diameter="58"
          ></mat-progress-spinner>
          <button mat-raised-button *ngIf="selectedRelease && !addingCommItems" (click)="onAdd()" name="addButton">
            ADD
          </button>
        </div>

        <mat-accordion *ngIf="selectedRelease" id="selected-release-info">
          <mat-expansion-panel class="bundles">
            <mat-expansion-panel-header>
              <mat-panel-title> Included Bundle/Folder(s) </mat-panel-title>
            </mat-expansion-panel-header>
            <mat-progress-spinner
              *ngIf="loadingCommItems"
              color="primary"
              mode="indeterminate"
              diameter="58"
            ></mat-progress-spinner>
            <table *ngIf="!loadingCommItems">
              <thead>
                <tr>
                  <th>Product Code</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let ci of commItemsPage" [ngClass]="{ 'expired-comm-item': isExpired(ci.expiration_date) }">
                  <td>{{ ci.comm_item_id }}</td>
                  <td>{{ ci.description }}</td>
                </tr>
              </tbody>
            </table>
            <mat-paginator
              *ngIf="!loadingCommItems && commItems.length > commItemsPageSize"
              [pageSize]="commItemsPageSize"
              [length]="commItems.length"
              (page)="handleCommItemsPageEvent($event)"
            >
            </mat-paginator>
          </mat-expansion-panel>
        </mat-accordion>
      </div>
    </mat-tab>
    <mat-tab label="Simulate Account" *ngIf="!impersonatedUserName && hasImpersonationPermission">
      <div>
        <button mat-stroked-button name="searchCustomers" class="search-customers" (click)="openSearchCustomers()">
          Search for Customer
        </button>
        <button mat-stroked-button name="searchCustomers" class="recent-customers" (click)="openRecentCustomers()">
          Recent Customers
        </button>
      </div>
      <div class="selected-customer">
        <div>
          <span class="customer-property">User ID: </span>
          <span>{{ selectedCustomer?.user_id }}</span>
        </div>
        <div>
          <span class="customer-property">E-mail: </span>
          <span>{{ selectedCustomer?.email }}</span>
        </div>
        <div>
          <span class="customer-property">Licenses: </span>
          <span>{{ selectedCustomer?.licenses.join(', ') }}</span>
        </div>
      </div>
      <div class="customer-bundles">
        <div class="customer-bundles-heading">Purchased Bundles</div>
        <mat-divider></mat-divider>
        <table>
          <thead>
            <tr>
              <th>Product Code</th>
              <th>Description</th>
              <th>Expiration Date</th>
            </tr>
          </thead>
          <mat-progress-spinner
            *ngIf="loadingSelectedCustomerCommItems"
            color="primary"
            mode="indeterminate"
          ></mat-progress-spinner>
          <tbody>
            <tr *ngFor="let b of selectedCustomerCommItemsPage">
              <td>{{ b.comm_item_id }}</td>
              <td>
                <div class="order-description-container">
                  <div *ngIf="isExpired(b.expiration_date)" class="expired-tag">[Expired]</div>
                  <div [ngClass]="{ 'expired-comm-item': isExpired(b.expiration_date) }">{{ b.description }}</div>
                </div>
              </td>
              <td>{{ b.expiration_date | date }}</td>
            </tr>
          </tbody>
        </table>
        <mat-paginator
          *ngIf="!!selectedCustomer && selectedCustomerCommItems.length > selectedCustomerCommItemsPageSize"
          [pageSize]="selectedCustomerCommItemsPageSize"
          [length]="selectedCustomerCommItems.length"
          (page)="handleSelectedCustomerCommItemsPageEvent($event)"
        >
        </mat-paginator>
      </div>
      <div>
        <div *ngIf="startingImpersonation" class="simulate">
          <mat-progress-spinner color="primary" mode="indeterminate" diameter="36"></mat-progress-spinner>
        </div>
        <button
          mat-raised-button
          name="simulate"
          class="simulate"
          [disabled]="!selectedCustomer || stoppingImpersonation"
          *ngIf="!startingImpersonation"
          (click)="impersonateUser()"
        >
          Simulate Customer Entitlement
        </button>
      </div>
    </mat-tab>
    <mat-tab label="Tool Sync Status" *ngIf="currentUserService.isAdmin()">
      <div class="selected-customer">
        <div>
          <span class="customer-property">Status: </span>
          <span>{{ toolSyncResult?.status }}</span>
        </div>
        <div>
          <span class="customer-property">Message: </span>
          <span>{{ toolSyncResult?.message }}</span>
        </div>
        <div>
          <span class="customer-property">Tool: </span>
          <span>{{ toolSyncResult?.tool }}</span>
        </div>
      </div>
      <div>
        <div *ngIf="startToolSync" class="simulate">
          <mat-progress-spinner color="primary" mode="indeterminate" diameter="36"></mat-progress-spinner>
        </div>
        <button
          mat-raised-button
          name="simulate"
          class="simulate"
          [disabled]="startToolSync"
          *ngIf="!startToolSync"
          (click)="onSync()"
        >
          <mat-icon>sync</mat-icon>
          Tool Sync
        </button>
      </div>
    </mat-tab>
  </mat-tab-group>

  <div class="product-list">
    <div class="heading">
      <div>
        My Products
        <span *ngIf="impersonatedUserName" class="impersonating">
          Impersonating {{ impersonatedUserName }}
          <mat-progress-spinner
            color="primary"
            mode="indeterminate"
            diameter="34"
            *ngIf="stoppingImpersonation"
            class="spinner"
          ></mat-progress-spinner>
          <button
            mat-mini-fab
            (click)="stopImpersonatingUser()"
            color="primary"
            *ngIf="!stoppingImpersonation && isInternal"
            matTooltip="Stop impersonating {{ impersonatedUserName }}"
          >
            <mat-icon>clear</mat-icon>
          </button>
        </span>
      </div>
      <div *ngIf="isInternal">
        <button
          *ngIf="!refreshingEntitlements"
          color="link"
          mat-mini-fab
          [disabled]="loadingEntitlements"
          (click)="onRefresh()"
          class="refresh-button"
          matTooltip="Refresh entitlements"
        >
          <mat-icon>refresh</mat-icon>
        </button>
      </div>
    </div>

    <div class="spinner-container" style="display: none">
      <mat-progress-spinner *ngIf="loadingEntitlements" color="primary" mode="indeterminate"></mat-progress-spinner>
    </div>

    <!-- Skeleton loader -->
    <div *ngIf="loadingEntitlements" class="skeleton-list" aria-busy="true" aria-label="Loading products">
      <div *ngFor="let i of [1, 2, 3]" class="skeleton-card">
        <div class="skeleton-line skeleton-title"></div>
        <div class="skeleton-line skeleton-body"></div>
        <div class="skeleton-line skeleton-body skeleton-body--short"></div>
      </div>
    </div>

    <!-- Error state -->
    <div *ngIf="!loadingEntitlements && entitlementsError" class="products-state-message">
      <mat-icon class="state-icon state-icon--error">error_outline</mat-icon>
      <div class="state-heading">Unable to load your products</div>
      <div class="state-body">There was a problem reaching the server. Check your connection and try again.</div>
      <button mat-raised-button color="primary" (click)="retryLoadEntitlements()">Retry</button>
    </div>

    <div *ngIf="!loadingEntitlements && !entitlementsError">
      <!-- eslint-disable-next-line @angular-eslint/template/eqeqeq -->
      <div *ngIf="entitledProducts.length == 0" class="products-state-message">
        <mat-icon class="state-icon">inventory_2</mat-icon>
        <div class="state-heading">No products found</div>
        <div class="state-body">
          Your account has no entitled products yet. Contact your
          <a href="https://www.windriver.com/contact" target="_blank">Wind River account team</a>
          to get access.
        </div>
      </div>
      <div *ngIf="entitledProducts.length > 0">
        <div class="instruction">
          Select the product you would like to install and pick the installation option that best works with your
          corporate network configuration and project requirements. Installers use HTTPS (port 443), so if your network
          blocks archive files (ZIP, UDF, or EXE) or restricts bandwidth on large downloads, contact your IT
          administrator to adjust proxy or firewall settings. In some cases, you may need to work with your IT
          department to adjust your network configuration to download and install products.
        </div>
        <div class="instruction">
          Additional installation and license setup instructions can be found by consulting the
          <a [href]="installLicenseUrl" target="_blank">Wind River Support Network</a>.
        </div>
      </div>

      <div *ngFor="let ep of entitledProducts">
        <div class="product-heading">
          {{ getProductName(ep.id) }}
          <mat-divider></mat-divider>
        </div>
        <mat-accordion [multi]="true" *ngFor="let r of ep.releases" class="entitlement">
          <mat-expansion-panel (opened)="onExpandReleasePanel(r)">
            <mat-expansion-panel-header>
              <div class="release-heading">
                <span #titleEl class="release-title" [matTooltip]="isTruncated(titleEl) ? r.name : ''">{{
                  r.name
                }}</span>
                <span
                  *ngIf="offlineInstallerStatus.get(r.cardKey)"
                  class="installer-status release-status"
                  [ngClass]="'installer-status--' + (offlineInstallerStatus.get(r.cardKey) | lowercase)"
                  >{{ formatInstallerStatus(offlineInstallerStatus.get(r.cardKey)) }}</span
                >
                <span *ngIf="!offlineInstallerStatus.get(r.cardKey)" class="release-status"></span>
                <div class="release-links">
                  <a class="open-dialog" (click)="openOrderDetails($event, r)">Order Details</a>
                  <a *ngIf="r.has_notes" class="open-dialog" (click)="openReleaseNotes($event, r)">Release Notes</a>
                </div>
              </div>
            </mat-expansion-panel-header>
            <mat-progress-spinner
              *ngIf="r.detailsState === 'loading'"
              color="primary"
              mode="indeterminate"
              diameter="50"
              class="mx-auto my-2"
            ></mat-progress-spinner>
            <ng-container *ngIf="r.detailsState === 'loaded'">
              <div *ngIf="r.all_comm_items_expired" class="d-flex justify-content-between">
                <div class="all-expired-message">
                  <ng-container *ngIf="isInternal && !impersonatedUserName; else customerExpiredMsg">
                    Access to this product has expired. Please add this product again in the Add Products menu above to
                    renew entitlement.
                  </ng-container>
                  <ng-template #customerExpiredMsg>
                    Access to this product requires an active support contract. For more information, please contact
                    your
                    <a href="https://www.windriver.com/contact">Wind River account team.</a>
                  </ng-template>
                </div>

                <div *ngIf="isDeletingProduct[r.id]">
                  <mat-progress-spinner color="primary" mode="indeterminate" class="delete-button mt-3" diameter="25">
                  </mat-progress-spinner>
                </div>
                <button
                  mat-icon-button
                  (click)="onDeleteRelease(r.id, r.name)"
                  class="delete-button pt-1"
                  *ngIf="
                    !isDeletingProduct[r.id] && isInternal && !impersonatedUserName && !r.release_eng_name_tag_eng_name
                  "
                  matTooltip="Remove from My Products"
                >
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
              <div *ngIf="!r.all_comm_items_expired" class="installation-options-container">
                <div class="options-heading">
                  <div>
                    <span>Installation Options</span>
                    <div *ngIf="r.installation_technology === 'GIT_CLONE'" class="open-dialog">
                      <a href="{{ r.gitlab_eng_repo_Url }}" target="_blank">(Browse in GitLab)</a>
                    </div>
                  </div>
                  <div *ngIf="isDeletingProduct[r.id]">
                    <mat-progress-spinner color="primary" mode="indeterminate" class="delete-button" diameter="25">
                    </mat-progress-spinner>
                  </div>
                  <button
                    mat-icon-button
                    (click)="onDeleteRelease(r.id, r.name)"
                    class="delete-button"
                    *ngIf="
                      !isDeletingProduct[r.id] &&
                      isInternal &&
                      !impersonatedUserName &&
                      !r.release_eng_name_tag_eng_name
                    "
                    matTooltip="Remove from My Products"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
                <div class="release-list">
                  <!-- Currently the is_folder flag here is being checked as a stand-in this will be updated in the future -->
                  <div
                    *ngIf="!r.isDisabled && r.installation_technology === 'FOLDER' && r.folders.length > 0"
                    class="folder-install release-list-item"
                  >
                    <div *ngIf="r.license_agreement.accepted !== undefined && !r.license_agreement.accepted">
                      <div>
                        <b>
                          You must accept the license agreement for
                          <a class="open-dialog-base" (click)="displayLicenseAgreement($event, r)"> {{ r.name }} </a>
                          to download this software.
                        </b>
                      </div>
                    </div>
                    <div *ngIf="r.license_agreement.accepted">
                      <div>
                        Thank you for accepting the license agreement; you may now download this software.
                        <!-- <a class="open-dialog-base" target="_blank" [href]="r.license_agreement.url"> -->
                        <a class="open-dialog-base" (click)="openLicenseInNewTab(r)">
                          View accepted license agreement
                        </a>
                      </div>
                    </div>
                    <br />
                    <div class="download-heading">Download Products</div>
                    <div>Click the <b>Download</b> button to see the list of files available to download.</div>
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let f of r.folders">
                          <td>{{ f.name }}</td>
                          <td>
                            <div *ngIf="r.license_agreement.accepted !== undefined && !r.license_agreement.accepted">
                              <button
                                mat-raised-button
                                class="base-installer-button"
                                (click)="displayLicenseAgreement($event, r)"
                              >
                                Download
                              </button>
                            </div>
                            <div *ngIf="r.license_agreement.accepted === undefined || r.license_agreement.accepted">
                              <a [href]="f.path" target="_blank" mat-raised-button class="installer-button">Download</a>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    *ngIf="!r.isDisabled && r.installation_technology === 'MANIFEST' && r.folders.length > 0"
                    class="folder-install release-list-item"
                  >
                    <div *ngIf="r.license_agreement.accepted !== undefined && !r.license_agreement.accepted">
                      <div>
                        <b>
                          You must accept the license agreement for
                          <a class="open-dialog-base" (click)="displayLicenseAgreement($event, r)"> {{ r.name }} </a>
                          to download this software.
                        </b>
                      </div>
                    </div>
                    <div *ngIf="r.license_agreement.accepted">
                      <div>
                        Thank you for accepting the license agreement; you may now download this software.
                        <!-- <a class="open-dialog-base" target="_blank" [href]="r.license_agreement.url"> -->
                        <a class="open-dialog-base" (click)="openLicenseInNewTab(r)">
                          View accepted license agreement
                        </a>
                      </div>
                    </div>
                    <br />
                    <div class="download-heading">Download Products</div>
                    <div>
                      Click <b>Download Manifest</b> to browse or download the manifest for this product release.
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let f of r.folders">
                          <td>{{ f.name }}</td>
                          <td>
                            <div *ngIf="r.license_agreement.accepted !== undefined && !r.license_agreement.accepted">
                              <button
                                mat-raised-button
                                class="base-installer-button"
                                (click)="displayLicenseAgreement($event, r)"
                              >
                                Download Manifest
                              </button>
                            </div>
                            <div *ngIf="r.license_agreement.accepted === undefined || r.license_agreement.accepted">
                              <a [href]="f.path" target="_blank" mat-raised-button class="installer-button"
                                >Download Manifest</a
                              >
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    *ngIf="
                      !r.isDisabled &&
                      r.installation_technology === 'GIT_CLONE' &&
                      r.eng_name !== 'wrstudio-developer-git-cd' &&
                      r.folders.length > 0
                    "
                    class="git-install release-list-item"
                  >
                    <div class="download-heading">Git Cloning: Recommended Option</div>
                    <div>
                      Follow the detailed instructions in the Wind River Linux Platform Development Quick Start using
                      the following command line settings:
                    </div>
                    <ul>
                      <li>
                        <b>repoURL</b> = <a href="{{ r.gitlab_repo_Url }}" target="_blank">{{ r.git_clone_Url }}</a>
                      </li>
                      <li><b>branchName</b> = {{ r.git_clone_release_branch_name }}</li>
                    </ul>
                    <div>These sample commands will help you when following the quick start guide:</div>
                    <div class="command-line-sample">
                      <pre>mkdir -p my-project<br />cd my-project<br />git clone --branch {{r.git_clone_release_branch_name}} \<br />{{r.git_clone_Url}}<br />./{{r.sample_command_folder_name}}/setup.sh --machines qemux86-64 --dl-layers</pre>
                    </div>
                    <div>
                      Host system requirements are included in the
                      <a href="{{ r.release_documentation_link }}" target="_blank"
                        >Wind River Linux Platform Development Quick Start.</a
                      >
                    </div>
                    <div *ngIf="loadingOfflineInstaller && !r.release_eng_name_tag_eng_name" class="release-list-item">
                      <div class="download-heading">
                        Offline Zip Installation
                        <a class="open-dialog" (click)="openOfflineInstallHistory($event, r)"
                          >Offline Installer Requests</a
                        >
                      </div>
                      <div>Generate installation content as .zip files.</div>
                      <div>
                        <button
                          *ngIf="!requestingOfflineInstaller[r.id]"
                          mat-raised-button
                          name="submitOfflineInstall"
                          class="submit-button"
                          (click)="generateOfflineInstaller(r)"
                        >
                          Submit
                        </button>
                        <mat-progress-spinner
                          *ngIf="requestingOfflineInstaller[r.id]"
                          color="primary"
                          mode="indeterminate"
                          diameter="29"
                        ></mat-progress-spinner>
                      </div>
                    </div>
                  </div>
                  <div
                    *ngIf="
                      !r.isDisabled &&
                      (r.installation_technology === 'RPM' || r.installation_technology === 'GIT') &&
                      r.folders.length > 0
                    "
                    class="online-offline-install"
                  >
                    <div class="release-list-item">
                      <div class="download-heading">Online Installation</div>
                      <div>Select the installer based on your host.</div>
                      <div class="installer-button-wrapper" *ngIf="!loadingOnlineInstaller[r.cardKey]">
                        <button
                          mat-raised-button
                          name="linuxInstaller"
                          class="installer-button"
                          (click)="downloadOnlineInstaller(r, 'x86-linux2')"
                        >
                          Installer for Linux
                        </button>
                        <button
                          mat-raised-button
                          name="windowsInstaller"
                          class="installer-button"
                          (click)="downloadOnlineInstaller(r, 'x86-win32')"
                        >
                          Installer for Windows
                        </button>
                      </div>
                      <div *ngIf="loadingOnlineInstaller[r.cardKey]">
                        <mat-progress-spinner color="primary" mode="indeterminate" diameter="58">
                        </mat-progress-spinner>
                        <span color="primary" *ngIf="loadingOnlineInstallerProgress[r.cardKey] <= 0"
                          >Please be patient, generating file...</span
                        >
                        <span color="primary" *ngIf="loadingOnlineInstallerProgress[r.cardKey] > 0"
                          >Getting file: ({{ loadingOnlineInstallerProgress[r.cardKey] }}%)</span
                        >
                      </div>
                    </div>
                    <div
                      *ngIf="
                        loadingOfflineInstaller &&
                        r.installation_technology === 'RPM' &&
                        !r.release_eng_name_tag_eng_name
                      "
                      class="release-list-item"
                    >
                      <div class="download-heading">
                        Offline Zip Installation
                        <a class="open-dialog" (click)="openOfflineInstallHistory($event, r)"
                          >Offline Installer Requests</a
                        >
                      </div>
                      <!--                  <div>Generate installation files as .zip (recommended) or .udf files. <a [routerLink]="['/help']" fragment="question5">How do I make an installation DVD?</a></div>-->
                      <div>Generate installation content as .zip files.</div>
                      <mat-progress-spinner
                        *ngIf="loadingReleaseVersions"
                        color="primary"
                        mode="indeterminate"
                        diameter="29"
                      ></mat-progress-spinner>
                      <wsn-searchable-select
                        class="version-selector"
                        *ngIf="!loadingReleaseVersions && r.versions.length > 0"
                        [allItems]="r.versions"
                        [selected]="r.selectedVersion"
                        (onChangeSelection)="r.selectedVersion = $event"
                        searchProperty="comm_name"
                        hint="Select a version"
                        [selectRequired]
                      ></wsn-searchable-select>
                      <div class="offline-type-selector">
                        <!--                    <div>Select file type:</div>-->
                        <!--                    <mat-button-toggle-group [value]="offlineInstallerType">-->
                        <!--                      <mat-button-toggle value="zip" (change)="onArchiveChoiceChange($event.value)">.zip file</mat-button-toggle>-->
                        <!--                      <mat-button-toggle value="udf" (change)="onArchiveChoiceChange($event.value)">.udf image</mat-button-toggle>-->
                        <!--                    </mat-button-toggle-group>-->
                      </div>
                      <div>
                        <button
                          *ngIf="!requestingOfflineInstaller[r.id] && r.selectedVersion"
                          mat-raised-button
                          name="submitOfflineInstall"
                          class="submit-button"
                          (click)="generateOfflineInstaller(r)"
                        >
                          Submit
                        </button>
                        <mat-progress-spinner
                          *ngIf="requestingOfflineInstaller[r.id]"
                          color="primary"
                          mode="indeterminate"
                          diameter="29"
                        ></mat-progress-spinner>
                      </div>
                    </div>
                  </div>
                  <div *ngIf="r.isDisabled" class="online-offline-install">
                    <b>
                      Installation options for this release are temporarily unavailable due to planned release
                      maintenance.
                    </b>
                  </div>
                  <div *ngIf="!r.isDisabled && r.folders.length === 0" class="online-offline-install">
                    <b>
                      No installation option is available for this release. Please contact your
                      <a href="https://www.windriver.com/contact">Wind River account team </a> for more information.
                    </b>
                  </div>
                  <div *ngIf="r.eng_name !== 'wrstudio-developer-git-cd'">
                    <div *ngIf="r.installation_technology !== 'MANIFEST'" class="container-registry-link">
                      <a mat-raised-button *ngIf="ep.includes_container_registry" [href]="harborUrl" target="_blank">
                        Visit Harbor for Container Registry
                      </a>
                    </div>
                    <div *ngIf="r.installation_technology === 'MANIFEST'" class="container-registry-link">
                      <a mat-raised-button *ngIf="ep.includes_container_registry" [href]="galleryUrl" target="_blank">
                        Visit Gallery for Installation
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </mat-expansion-panel>
        </mat-accordion>
      </div>
    </div>
  </div>
</div>
